import React, { useEffect, useState } from 'react';
import { Box, Button, FormControl, IconButton, MenuItem, Paper, Select, TextField, Typography } from '@mui/material';
import ShireTable from 'components/ShireTable';
import { ProductDefaultValues, ProductTableColumns } from './ProductModel';
import { PaginationModel } from 'utils/PaginationType';
import { Addon, Category, Material, Product, ProductOption, Size } from './ProductType';
import { ProductModal } from './ProductModal';
import { deleteProduct, getAddonOptions, getCategorieOptions, getMaterialOptions, getProducts, getSizeOptions } from 'services/ProductService';
import CategoryManagement from './CategoryManagement';
import { Search } from '@mui/icons-material';
import { useConfirmDialog } from 'hooks/useConfirmDialog';


export type SearchProductParams = {
  search: string;
  category: string;
}

const ProductManagement: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [paginationModel, setPaginationModel] = useState<PaginationModel>({
    page: 0,
    pageSize: 10,
    rowCount: 0,
  });
  const [products, setProducts] = useState<any[]>([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [initialValues, setInitialValues] = useState<Product>({ ...ProductDefaultValues });
  const [isEdit, setIsEdit] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState<ProductOption[]>([]);
  const [sizeOptions, setSizeOptions] = useState<ProductOption[]>([]);
  const [materialOptions, setMaterialOptions] = useState<ProductOption[]>([]);
  const [addonOptions, setAddonOptions] = useState<ProductOption[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [searchParams, setSearchParams] = useState<SearchProductParams>({
    search: '',
    category: '',
  });
  const { openConfirmDialog, closeConfirmDialog } = useConfirmDialog();

  const fetchProducts = async (page: number, pageSize: number, search: SearchProductParams) => {
    const response = await getProducts(page, pageSize, search);
    setProducts(response.records);
    setTotalProducts(response.total);
  };

  useEffect(() => {
    fetchCategoryOptions();
    fetchSizeOptions();
    fetchMaterialOptions();
    fetchAddonOptions();
  }, []);

  useEffect(() => {
    fetchProducts(paginationModel.page + 1, paginationModel.pageSize, searchParams);
  }, [paginationModel.page, paginationModel.pageSize, searchParams]);

  const fetchCategoryOptions = async () => {
    const response = await getCategorieOptions();
    setCategoryOptions(response.map((category: Category) => ({
      id: category.id,
      name: category.name,
      code: category.code,
      price: 0,
    })));
  };

  const fetchSizeOptions = async () => {
    const response = await getSizeOptions();
    setSizeOptions(response.map((size: Size) => ({
      id: size.id,
      name: size.name,
      code: '',
      price: size.price,
    })));
  };

  const fetchMaterialOptions = async () => {
    const response = await getMaterialOptions();
    setMaterialOptions(response.map((material: Material) => ({
      id: material.id,
      name: material.name,
      code: '',
      price: material.price,
    })));
  };

  const fetchAddonOptions = async () => {
    const response = await getAddonOptions();
    setAddonOptions(response.map((addon: Addon) => ({
      id: addon.id,
      name: addon.name,
      code: '',
      price: addon.price,
    })));
  };

  const handleEdit = (product: Product) => {
    setInitialValues(product);
    setIsEdit(true);
    setOpen(true);
  };

  const handleDelete = (product: Product) => {
    openConfirmDialog({
      title: 'Delete Product',
      content: `Are you sure you want to delete ${product.name}?`,
      handleOnOk: async () => {
        await onDeleteProduct(product.id);
        closeConfirmDialog();
      },
    });
  };

  const onDeleteProduct = async (id: number) => {
    await deleteProduct(id);
    await fetchProducts(paginationModel.page + 1, paginationModel.pageSize, searchParams);
  };

  const handlePostSubmit = async () => {
    await fetchProducts(paginationModel.page + 1, paginationModel.pageSize, searchParams);
    setOpen(false);
    setIsEdit(false);
    setInitialValues({ ...ProductDefaultValues });
  };

  const handleSearch = () => {
    setSearchParams(prev => ({ ...prev, search: searchValue }));
  };

  const onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return <>
    <Typography variant="h4" sx={{ marginBottom: 3 }}>Quản lý sản phẩm</Typography>
    <Paper elevation={3} sx={{ padding: 2, marginBottom: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
            <FormControl sx={{ flex: 1, position: 'relative' }}>
              <TextField
                id="search"
                name="search"
                placeholder="Tìm kiếm theo tên"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyUp={onKeyUp}
              />
            </FormControl>
            <IconButton
              color="primary"
              onClick={handleSearch}
              disabled={searchParams.search === ''}
              sx={{ position: 'absolute', right: 0 }}
            >
              <Search />
            </IconButton>
          </Box>
          <FormControl>
            <Select
              id="categories"
              name="categories"
              value={searchParams.category}
              onChange={(e) => setSearchParams(prev => ({ ...prev, category: e.target.value.toString() }))}
              displayEmpty
            >
              <MenuItem value="">
                <em>Tất cả danh mục</em>
              </MenuItem>
              {categoryOptions.map((category) => (
                <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
          Thêm mới sản phẩm
        </Button>
      </Box>

      <ShireTable
        columns={ProductTableColumns(fetchProducts, paginationModel, handleEdit, handleDelete)}
        rows={products}
        paginationModel={{
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          rowCount: totalProducts,
        }}
        onPaginationModelChange={setPaginationModel}
      />
    </Paper>
    <CategoryManagement />
    <ProductModal
      open={open}
      onClose={() => setOpen(false)}
      onSubmit={() => handlePostSubmit()}
      initialValues={initialValues}
      isEdit={isEdit}
      categories={categoryOptions}
      sizes={sizeOptions}
      materials={materialOptions}
      addons={addonOptions}
    />
  </>;
};

export default ProductManagement; 